<script setup lang="ts">
import { computed } from 'vue'

import { useSummaryOverview } from '../composables/useSummaryOverview'
import { buildSummaryOverviewChartOptions } from '../service/summaryOverview'

import OverviewControls from './OverviewControls.vue'
import OverviewMetricCard from './OverviewMetricCard.vue'
import OverviewSkeleton from './skeletons/OverviewSkeleton.vue'
import { summaryPanelClass } from './summaryPanelClasses'

import {
  MatrixTable,
  toReportColumnOption,
  useReportCellFormatter,
  type ReportColumn,
  type ReportRow,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Button, Icon, type SelectOption } from '@/shared/ui'
import { ChartRoot } from '@/shared/ui/chart'

const props = withDefaults(defineProps<{ defaultPostData?: string }>(), { defaultPostData: '' })
const { t } = useI18n()
const format = useFormat()
const { formatCell } = useReportCellFormatter()
const query = computed(() => props.defaultPostData)
const overview = useSummaryOverview(query)
/** First fetch only: a refetch over a drawn series keeps it on screen instead of re-skeletoning. */
const coldLoading = computed(() => overview.state.value === 'loading' && !overview.series.value)

const selectedMetrics = computed(() =>
  overview.selectedMetricFields.value.flatMap((field) => {
    const metric = overview.metrics.value.find((item) => item.field === field)
    return metric ? [metric] : []
  }),
)
const frequencyOptions = computed(() =>
  overview.frequencyOptions.map((option) => ({ ...option, label: t(option.label) })),
)
const chartOptions = computed(() => {
  if (!overview.series.value) return null
  return buildSummaryOverviewChartOptions({
    series: overview.series.value,
    forecast: overview.forecastSeries.value,
    metrics: selectedMetrics.value,
    activeMetricFields: overview.activeMetricFields.value,
    activePlatform: overview.activePlatform.value,
    frequency: overview.displayedFrequency.value,
    showForecast: overview.forecastEnabled.value,
    formatCell,
    formatDate: (value) => format.formatDate(value),
    formatAxisValue: (value) => format.formatCompact(value, { maxFrac: 0 }).replace('K', 'k'),
    previousPeriodLabel: t('summary.overview.previousPeriod'),
    forecastLabel: t('summary.overview.forecast'),
  })
})
const platformColumns = computed<ReportColumn[]>(() => [
  { field: 'platform', header: t('summary.overview.platform'), frozen: true },
  ...selectedMetrics.value,
])
const platformRows = computed<ReportRow[]>(() =>
  overview.platforms.value.map((platform) => ({
    platform,
    ...Object.fromEntries(
      selectedMetrics.value.map((metric) => [
        metric.field,
        overview.series.value?.[`${platform}_${metric.field}`]?.current_total ?? null,
      ]),
    ),
  })),
)
const metricsAvailableToAdd = computed(() => {
  const selected = new Set(overview.selectedMetricFields.value)
  return overview.metrics.value.filter((metric) => !selected.has(metric.field))
})

function metricOptions(metric: ReportColumn): SelectOption<string>[] {
  const otherFields = new Set(
    overview.selectedMetricFields.value.filter((field) => field !== metric.field),
  )
  return overview.metrics.value
    .filter((item) => !otherFields.has(item.field))
    .map((item) => toReportColumnOption(item))
}

function toggleMetric(field: string): void {
  const active = overview.activeMetricFields.value
  if (active.includes(field)) {
    if (active.length > 1)
      overview.activeMetricFields.value = active.filter((item) => item !== field)
    return
  }
  overview.activeMetricFields.value = [...active, field]
}

function removeMetric(field: string): void {
  overview.selectedMetricFields.value = overview.selectedMetricFields.value.filter(
    (item) => item !== field,
  )
  overview.activeMetricFields.value = overview.activeMetricFields.value.filter(
    (item) => item !== field,
  )
}

function changeMetric(previous: string, next: string): void {
  overview.selectedMetricFields.value = overview.selectedMetricFields.value.map((field) =>
    field === previous ? next : field,
  )
  overview.activeMetricFields.value = overview.activeMetricFields.value.map((field) =>
    field === previous ? next : field,
  )
}

function addMetric(): void {
  const metric = metricsAvailableToAdd.value[0]
  if (!metric) return
  overview.selectedMetricFields.value = [...overview.selectedMetricFields.value, metric.field]
}
</script>

<template>
  <section
    :class="[summaryPanelClass, 'relative mb-0']"
    :aria-busy="overview.state.value === 'loading' || undefined"
  >
    <!-- Controls stay mounted in every state — v3 `SummaryDashboardChart` kept its Group/Frequency
         row live between the two `DashboardSkeleton` halves — so a frequency/group change can also
         recover a failed or empty fetch; only the metric rail and the chart body swap. -->
    <OverviewSkeleton v-if="coldLoading" part="metrics" />
    <div v-else-if="overview.series.value" class="mb-sm flex w-full gap-3 overflow-x-auto pb-2">
      <OverviewMetricCard
        v-for="(metric, index) in selectedMetrics"
        :key="`metric-slot-${index}`"
        :metric="metric"
        :options="metricOptions(metric)"
        :active="overview.activeMetricFields.value.includes(metric.field)"
        :color="`var(--ui-chart-summary-${(index % 5) + 1})`"
        :current-total="
          overview.series.value[`${overview.activePlatform.value}_${metric.field}`]
            ?.current_total ?? 0
        "
        :previous-total="
          overview.series.value[`${overview.activePlatform.value}_${metric.field}`]?.before_total ??
          0
        "
        :formatted-value="
          formatCell(
            overview.series.value[`${overview.activePlatform.value}_${metric.field}`]
              ?.current_total,
            metric,
          )
        "
        :remove-label="t('summary.overview.removeMetric', { metric: metric.header })"
        @toggle="toggleMetric(metric.field)"
        @remove="removeMetric(metric.field)"
        @change="changeMetric(metric.field, $event)"
      />
      <Button
        v-if="metricsAvailableToAdd.length"
        class="summary-overview__add-metric !w-[3.625rem] !min-w-[3.625rem] min-h-14 self-stretch border border-border rounded-md bg-surface shadow-pop"
        variant="ghost"
        square
        :aria-label="t('summary.overview.addMetric')"
        @click="addMetric"
      >
        <Icon name="plus" :size="16" />
      </Button>
    </div>

    <OverviewControls
      v-model:group="overview.selectedGroup.value"
      v-model:frequency="overview.selectedFrequency.value"
      v-model:campaign="overview.selectedCampaign.value"
      v-model:channels="overview.selectedChannels.value"
      v-model:forecast="overview.forecastEnabled.value"
      :group-options="overview.groupOptions.value"
      :frequency-options="frequencyOptions"
      :campaign-options="overview.campaignOptions.value"
      :channel-options="overview.channelOptions.value"
      :has-forecast="overview.hasForecast.value"
      :group-label="t('summary.overview.group')"
      :frequency-label="t('summary.overview.frequency.label')"
      :campaign-label="t('summary.overview.campaign')"
      :channel-label="t('summary.overview.channel')"
      :forecast-label="t('summary.overview.forecast')"
    />

    <OverviewSkeleton v-if="coldLoading" part="chart" />

    <div
      v-else-if="overview.state.value === 'error'"
      class="summary-overview__state flex min-h-[22rem] flex-col items-center justify-center gap-sm text-muted"
      role="alert"
    >
      <p>{{ t('summary.states.error') }}</p>
      <Button size="sm" variant="outline" @click="overview.reload">
        {{ t('summary.actions.retry') }}
      </Button>
    </div>

    <div
      v-else-if="overview.state.value === 'empty' || !overview.series.value"
      class="summary-overview__state flex min-h-[22rem] flex-col items-center justify-center gap-sm text-muted"
    >
      {{ t('summary.states.empty') }}
    </div>

    <template v-else>
      <div class="w-full h-[25rem]">
        <ChartRoot
          :options="chartOptions"
          :load-error-message="t('summary.states.error')"
          :retry-label="t('summary.actions.retry')"
          @retry="overview.reload"
        />
      </div>

      <MatrixTable
        v-if="overview.platforms.value.length > 1"
        class="mt-sm"
        v-bind="{ ariaLabel: t('summary.overview.platformComparison') }"
        :rows="platformRows"
        :columns="platformColumns"
      >
        <template #cell="{ column, value, row }">
          <button
            v-if="column.field === 'platform'"
            type="button"
            class="p-0 border-0 bg-transparent text-fg cursor-pointer"
            :class="{ 'text-primary font-bold': row.platform === overview.activePlatform.value }"
            @click="overview.activePlatform.value = String(row.platform)"
          >
            {{ value }}
          </button>
          <template v-else>{{ formatCell(value, column) }}</template>
        </template>
      </MatrixTable>
    </template>
  </section>
</template>

<style scoped>
.ui-chart {
  width: 100%;
  height: 100%;
}
</style>
